import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import { createTheme, ThemeProvider, Theme } from '@mui/material/styles';

import { CssBaseline } from '@mui/material';

import { darkComponentOverrides, darkPaletteOptions } from '../styles/theme';
import { copaComponentOverrides, copaPaletteOptions } from '../styles/customThemes/copa';
import { rosaComponentOverrides, rosaPaletteOptions } from '../styles/customThemes/rosa';
import { usaComponentOverrides, usaPaletteOptions } from '../styles/customThemes/usa';

import {

  AppThemeId,

  readStoredThemeId,

  resolveIsDarkMode,

  getBodyThemeClass,

  persistLegacyThemeKey,

  THEME_STORAGE_KEY,

} from '../themes/themeRegistry';

import { syncSwalTheme } from '../utils/swalTheme';



interface ThemeContextType {

  themeId: AppThemeId;

  setThemeId: (id: AppThemeId) => void;

  isDarkMode: boolean;

  toggleTheme: () => void;

  theme: Theme;

}



const ThemeContext = createContext<ThemeContextType | undefined>(undefined);



const sharedTypography = {

  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',

  h4: { fontWeight: 600 },

  h5: { fontWeight: 600 },

  h6: { fontWeight: 600 },

};



const lightTheme = createTheme({

  palette: {

    mode: 'light',

    primary: {

      main: '#0151C4',

      light: '#4285f4',

      dark: '#0d5bdb',

    },

    secondary: {

      main: '#dc004e',

      light: '#ff5983',

      dark: '#9a0036',

    },

    background: {

      default: '#f5f5f5',

      paper: '#ffffff',

    },

    text: {

      primary: '#333333',

      secondary: '#666666',

    },

  },

  typography: sharedTypography,

  shape: { borderRadius: 8 },

  components: {

    MuiDrawer: {

      styleOverrides: {

        paper: {

          backgroundColor: '#ffffff',

          borderRight: '1px solid #e0e0e0',

        },

      },

    },

    MuiAppBar: {

      styleOverrides: {

        root: {

          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',

        },

      },

    },

  },

});



const darkTheme = createTheme({

  palette: darkPaletteOptions,

  typography: sharedTypography,

  shape: { borderRadius: 8 },

  components: darkComponentOverrides,

});

const copaTheme = createTheme({

  palette: copaPaletteOptions,

  typography: sharedTypography,

  shape: { borderRadius: 8 },

  components: copaComponentOverrides,

});

const rosaTheme = createTheme({

  palette: rosaPaletteOptions,

  typography: sharedTypography,

  shape: { borderRadius: 8 },

  components: rosaComponentOverrides,

});

const usaTheme = createTheme({

  palette: usaPaletteOptions,

  typography: sharedTypography,

  shape: { borderRadius: 8 },

  components: usaComponentOverrides,

});



const themesById: Record<AppThemeId, Theme> = {

  default: lightTheme,

  dark: darkTheme,

  copa: copaTheme,

  rosa: rosaTheme,

  usa: usaTheme,

};



interface ThemeProviderProps {

  children: ReactNode;

}



export const CustomThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {

  const [themeId, setThemeIdState] = useState<AppThemeId>(() => readStoredThemeId());

  const isDarkMode = resolveIsDarkMode(themeId);

  syncSwalTheme(themeId);



  const setThemeId = (id: AppThemeId) => {

    setThemeIdState(id);

    localStorage.setItem(THEME_STORAGE_KEY, id);

    localStorage.setItem('sysup-theme', persistLegacyThemeKey(id));

  };



  const toggleTheme = () => {

    setThemeId(isDarkMode ? 'default' : 'dark');

  };



  const theme = themesById[themeId];



  useEffect(() => {

    document.body.className = getBodyThemeClass(themeId);

    document.documentElement.style.colorScheme = isDarkMode ? 'dark' : 'light';

    syncSwalTheme(themeId);

  }, [themeId, isDarkMode]);



  return (

    <ThemeContext.Provider

      value={{ themeId, setThemeId, isDarkMode, toggleTheme, theme }}

    >

      <ThemeProvider theme={theme}>

        <CssBaseline />

        {children}

      </ThemeProvider>

    </ThemeContext.Provider>

  );

};



export const useTheme = (): ThemeContextType => {

  const context = useContext(ThemeContext);

  if (context === undefined) {

    throw new Error('useTheme deve ser usado dentro de um CustomThemeProvider');

  }

  return context;

};

